import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { API_BASE_URL } from "../services/api";

interface StaffReport {
  staff_id: number;
  staff_name: string;
  total_washes: number;
  avg_rating: number;
  efficiency: number;
}

const StaffReports: React.FC = () => {
  const [reports, setReports] = useState<StaffReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [totalWashes, setTotalWashes] = useState(0);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append("from_date", dateRange.from);
      if (dateRange.to) params.append("to_date", dateRange.to);

      const response = await fetch(
        `${API_BASE_URL}/reports/staff?${params.toString()}`
      );
      const data = await response.json();

      if (data?.status === "success") {
        const rows = data?.data?.listing?.rows || [];

        const formatted = rows.map((item: any) => ({
          staff_id: item.employee_id,
          staff_name: item.employee_name,
          total_washes: Number(item.total_washes || 0),
          avg_rating: Number(item.avg_provider_rating || 0),
          efficiency: Number(item.efficiency || 0),
        }));

        // Calculate total washes
      const total = formatted.reduce(
        (sum: number, r: StaffReport) => sum + (r.total_washes || 0),
        0
      );


        setReports(formatted);
        setTotalWashes(total);
      } else {
        console.error("Failed to fetch staff report:", data?.message);
        setReports([]);
        setTotalWashes(0);
      }
    } catch (error) {
      console.error("Error fetching staff report:", error);
      setReports([]);
      setTotalWashes(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  return (
    <div >
      <div style={{ display: "flex", gap: "10px", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
       

        <Typography variant="h4" gutterBottom>
                          Staff / Employee Reports
                </Typography>
        <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
          
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.from}
              onChange={(e) =>
                setDateRange({ ...dateRange, from: e.target.value })
              }
            />
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.to}
              onChange={(e) =>
                setDateRange({ ...dateRange, to: e.target.value })
              }
            />
            <Button
              variant="contained"
              color="primary"
              onClick={fetchReport}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>
      </div>
      {/* Filter Section */}
      
      {/* Report Table */}
      <Card>
        <CardContent style={{ padding: "0" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CircularProgress />
            </div>
          ) : (
            
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total Washes Completed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Average Rating</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Efficiency (Washes/hr)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length > 0 ? (
                  <>
                    {reports.map((r) => (
                      <TableRow key={r.staff_id}>
                        <TableCell>{r.staff_name}</TableCell>
                        <TableCell>{r.total_washes}</TableCell>
                        <TableCell>
                          {r.avg_rating ? r.avg_rating.toFixed(2) : "-"}
                        </TableCell>
                        <TableCell>
                          {r.efficiency ? r.efficiency.toFixed(2) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Total Summary Row */}
                    <TableRow >
                      <TableCell  sx={{ fontWeight: 600 }}> Total </TableCell>
                      <TableCell  sx={{ fontWeight: 600 }}> {totalWashes} </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffReports;
